import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

function MintOnApproveDialog({ request, onClose, onDone }) {
  const [templateId, setTemplateId] = useState("");
  const [note, setNote] = useState(`Approved your idea "${request.title}".`);
  const { data: templates } = useQuery({ queryKey: ["admin-templates", "all"], queryFn: async () => (await api.get("/admin/templates?page_size=1000")).data.items });

  const submit = async () => {
    try {
      const r = await api.post(`/admin/requests/${request.id}/review`, {
        status: "approved",
        admin_note: note,
        template_id: templateId || null,
      });
      if (r.data.minted_card) toast.success(`Minted ${r.data.minted_card.title} (${r.data.minted_card.serial}) to ${request.user_name}`);
      else toast.success("Request approved");
      onClose();
      onDone?.();
    } catch (e) { toast.error(apiError(e, "Approve failed")); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-[0.08em]">APPROVE REQUEST</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-white/70">Requester: <span className="text-white">{request.user_name}</span></div>
          <div className="text-sm text-white/70">Title: <span className="text-white">{request.title}</span></div>
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Mint Template (optional)</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="approve-template-select" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue placeholder="Approve without minting" /></SelectTrigger>
              <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10 max-h-[300px]">
                {(templates || []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.title} ({t.rarity}) — {t.minted_count}/{t.edition_count}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-white/40 mt-1">If selected, this template will be minted directly to the requester upon approval.</div>
          </div>
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Note to requester</Label>
            <Input data-testid="approve-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
          </div>
          <Button data-testid="approve-submit-button" onClick={submit} className="w-full bg-emerald-400 text-black hover:bg-emerald-300">
            <Check className="h-4 w-4 mr-1" /> {templateId ? "Approve + Mint" : "Approve"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminRequests() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("pending");
  const [denyNotes, setDenyNotes] = useState({});
  const [approving, setApproving] = useState(null);
  const { data: requests } = useQuery({ queryKey: ["admin-requests", filter], queryFn: async () => (await api.get(`/admin/requests${filter !== "all" ? `?status_filter=${filter}` : ""}`)).data });
  const deny = useMutation({
    mutationFn: async ({ id, admin_note }) => (await api.post(`/admin/requests/${id}/review`, { status: "denied", admin_note })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-requests"] }); toast.success("Request denied"); },
    onError: (e) => toast.error(apiError(e, "Failed")),
  });
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-3xl text-white tracking-[0.06em]">REQUESTS</h1>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="bg-[hsl(220_18%_10%)] border border-white/10">
            {["pending", "approved", "denied", "all"].map((s) => (
              <TabsTrigger key={s} value={s} data-testid={`admin-requests-filter-${s}`} className="uppercase tracking-[0.2em] text-[10px]">{s}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="space-y-3">
        {(requests || []).map((r) => (
          <div key={r.id} className="hud-pill rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="font-display text-xl text-white tracking-[0.06em]">{r.title}</div>
                <div className="text-xs text-white/40 mt-1">From: {r.user_name} ({r.user_email})</div>
                <div className="text-sm text-white/65 mt-2">{r.description}</div>
                {r.reference_url && <a className="text-cyan-300 text-xs mt-1 break-all inline-block" href={r.reference_url} target="_blank" rel="noreferrer">{r.reference_url}</a>}
                <div className="mt-2 text-xs font-mono uppercase tracking-[0.2em]" style={{ color: r.status === "approved" ? "#34d399" : r.status === "denied" ? "#f87171" : "#fbbf24" }}>{r.status}</div>
                {r.minted_card_id && (
                  <div className="mt-2 text-xs text-emerald-300 inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> Minted card to user</div>
                )}
              </div>
              {r.status === "pending" && (
                <div className="flex flex-col gap-2 w-72">
                  <Button data-testid={`approve-request-${r.id}`} onClick={() => setApproving(r)} className="bg-emerald-400 text-black hover:bg-emerald-300"><Check className="h-4 w-4 mr-1" /> Approve / Mint</Button>
                  <div className="flex gap-2">
                    <Input placeholder="Deny reason" value={denyNotes[r.id] || ""} onChange={(e) => setDenyNotes({ ...denyNotes, [r.id]: e.target.value })} className="bg-[hsl(220_18%_12%)] border-white/10 text-sm flex-1" />
                    <Button data-testid={`deny-request-${r.id}`} variant="outline" onClick={() => deny.mutate({ id: r.id, admin_note: denyNotes[r.id] || "" })} className="border-white/15 text-white"><X className="h-4 w-4" /></Button>
                  </div>
                </div>
              )}
              {r.status !== "pending" && r.admin_note && (
                <div className="text-xs text-cyan-300/80 italic max-w-xs">Admin: {r.admin_note}</div>
              )}
            </div>
          </div>
        ))}
        {(requests || []).length === 0 && <div className="hud-pill rounded-2xl p-10 text-center text-white/40">No requests {filter !== "all" ? `(${filter})` : ""}.</div>}
      </div>

      {approving && (
        <MintOnApproveDialog
          request={approving}
          onClose={() => setApproving(null)}
          onDone={() => qc.invalidateQueries({ queryKey: ["admin-requests"] })}
        />
      )}
    </div>
  );
}
